import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Shield, AlertCircle, CheckCircle, Copy, Eye, EyeOff,
    Loader2, Clock, Info, Lock
} from 'lucide-react';
import { apiMethods } from '@/services/api';

const Security = () => {
    const queryClient = useQueryClient();
    const [showEnableModal, setShowEnableModal] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');
    const [copied, setCopied] = useState(false);
    const [errors, setErrors] = useState({});

    // Fetch user profile for 2FA status
    const { data: profileData, isLoading: profileLoading } = useQuery({
        queryKey: ['userProfile'],
        queryFn: async () => {
            const response = await apiMethods.users.getProfile();
            return response.data;
        }
    });

    const user = profileData?.user;

    // Fetch user stats for security features
    const { data: statsData, isLoading: statsLoading } = useQuery({
        queryKey: ['userStats'],
        queryFn: async () => {
            const response = await apiMethods.users.getStats();
            return response.data;
        }
    });

    const securityFeatures = statsData?.stats?.securityFeatures;

    // Fetch login history
    const { data: loginHistoryData } = useQuery({
        queryKey: ['loginHistory'],
        queryFn: async () => {
            const response = await apiMethods.users.getLoginHistory();
            return response.data;
        }
    });

    // Enable 2FA setup mutation
    const setup2FAMutation = useMutation({
        mutationFn: async () => {
            const response = await apiMethods.auth.setup2FA();
            return response.data;
        },
        onSuccess: (data) => {
            setQrCode(data.qrCode);
            setSecret(data.secret);
            setShowEnableModal(true);
            setErrors({});
        },
        onError: (error) => {
            setErrors({ setup: error.response?.data?.message || 'Failed to setup 2FA' });
        }
    });

    // Enable 2FA verification mutation
    const verify2FAMutation = useMutation({
        mutationFn: async ({ secret, token }) => {
            const response = await apiMethods.auth.verify2FA({ secret, token });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['userProfile']);
            queryClient.invalidateQueries(['userStats']);
            setShowEnableModal(false);
            setVerificationCode('');
            setSecret('');
            setQrCode('');
            setErrors({});
        },
        onError: (error) => {
            setErrors({ verification: error.response?.data?.message || 'Invalid verification code' });
        }
    });

    // Disable 2FA mutation
    const disable2FAMutation = useMutation({
        mutationFn: async ({ password, token }) => {
            const response = await apiMethods.auth.disable2FA({ password, token });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['userProfile']);
            queryClient.invalidateQueries(['userStats']);
            setShowDisableModal(false);
            setVerificationCode('');
            setPassword('');
            setErrors({});
        },
        onError: (error) => {
            setErrors({ disable: error.response?.data?.message || 'Invalid credentials' });
        }
    });

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleEnable2FA = () => {
        if (!verificationCode) {
            setErrors({ verification: 'Verification code is required' });
            return;
        }
        verify2FAMutation.mutate({ secret, token: verificationCode });
    };

    const handleDisable2FA = () => {
        if (!password) {
            setErrors({ disable: 'Password is required' });
            return;
        }
        if (!verificationCode) {
            setErrors({ disable: 'Verification code is required' });
            return;
        }
        disable2FAMutation.mutate({ password, token: verificationCode });
    };

    if (profileLoading || statsLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="animate-spin h-8 w-8 text-primary-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Security Settings</h1>
                    <p className="mt-2 text-gray-600">
                        Manage your account security and authentication settings
                    </p>
                </div>

                {errors.setup && (
                    <div className="mb-6 rounded-md bg-red-50 p-4">
                        <div className="flex">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                            <p className="ml-3 text-sm text-red-800">{errors.setup}</p>
                        </div>
                    </div>
                )}

                {/* Two-Factor Authentication */}
                <div className="bg-white shadow rounded-lg p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                            <Shield className="h-6 w-6 text-primary-600 mr-3" />
                            <div>
                                <h2 className="text-lg font-medium text-gray-900">Two-Factor Authentication</h2>
                                <p className="text-sm text-gray-500">
                                    Add an extra layer of security to your account
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center">
                            {user?.twoFactorEnabled ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Enabled
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Disabled
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="mt-6">
                        {user?.twoFactorEnabled ? (
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    Two-factor authentication is currently enabled on your account.
                                </p>
                                <button
                                    onClick={() => setShowDisableModal(true)}
                                    className="px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                                >
                                    Disable 2FA
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    Protect your account with two-factor authentication using an authenticator app like Google Authenticator or Authy.
                                </p>
                                <button
                                    onClick={() => setup2FAMutation.mutate()}
                                    disabled={setup2FAMutation.isPending}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {setup2FAMutation.isPending ? 'Setting up...' : 'Enable Two-Factor Authentication'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Account Security Overview */}
                <div className="bg-white shadow rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Security Overview</h2>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-600">Two-Factor Authentication</span>
                            <span className={`text-sm font-medium ${
                                securityFeatures?.twoFactorEnabled ? 'text-green-600' : 'text-gray-500'
                            }`}>
                                {securityFeatures?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-600">Passkeys</span>
                            <span className="text-sm font-medium text-gray-900">
                                {securityFeatures?.passkeysCount || 0} configured
                            </span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-600">Account Created</span>
                            <span className="text-sm text-gray-900">
                                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-600">Last Login</span>
                            <span className="text-sm text-gray-900">
                                {loginHistoryData?.lastLogin ? new Date(loginHistoryData.lastLogin).toLocaleString() : 'Unknown'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Recent Login Activity */}
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="mb-4">
                        <h2 className="text-lg font-medium text-gray-900">Recent Login Activity</h2>
                        <p className="text-sm text-gray-500">
                            Your recent login history
                        </p>
                    </div>

                    {loginHistoryData?.loginHistory && loginHistoryData.loginHistory.length > 0 ? (
                        <div className="space-y-3">
                            {loginHistoryData.loginHistory.slice(0, 10).map((login, index) => (
                                <div key={index} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-0">
                                    <div className="flex items-start space-x-3">
                                        <div className={`mt-0.5 h-2 w-2 rounded-full ${
                                            login.success ? 'bg-green-400' : 'bg-red-400'
                                        }`} />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {login.success ? 'Successful login' : 'Failed login attempt'}
                                            </p>
                                            <div className="mt-1 text-xs text-gray-500 space-y-1">
                                                <p className="flex items-center">
                                                    <Clock className="h-3 w-3 mr-1" />
                                                    {new Date(login.timestamp).toLocaleString()}
                                                </p>
                                                {login.ip && (
                                                    <p>IP: {login.ip}</p>
                                                )}
                                                {login.method && (
                                                    <p>Method: {login.method}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No recent login activity to display.</p>
                    )}
                </div>

                {/* Enable 2FA Modal */}
                {showEnableModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Enable Two-Factor Authentication</h3>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm text-gray-600 mb-3">
                                        1. Scan this QR code with your authenticator app:
                                    </p>
                                    {qrCode && (
                                        <div className="bg-gray-100 p-4 rounded-lg flex justify-center">
                                            <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <p className="text-sm text-gray-600 mb-2">
                                        2. Or enter this code manually:
                                    </p>
                                    <div className="flex items-center space-x-2">
                                        <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono break-all">
                                            {secret}
                                        </code>
                                        <button
                                            onClick={() => copyToClipboard(secret)}
                                            className="p-2 text-gray-600 hover:text-gray-700"
                                        >
                                            {copied ? (
                                                <CheckCircle className="h-5 w-5 text-green-600" />
                                            ) : (
                                                <Copy className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-sm text-gray-600 mb-2">
                                        3. Enter the 6-digit code from your app:
                                    </p>
                                    <input
                                        type="text"
                                        value={verificationCode}
                                        onChange={(e) => {
                                            setVerificationCode(e.target.value);
                                            setErrors({});
                                        }}
                                        maxLength="6"
                                        className={`block w-full px-3 py-2 border ${
                                            errors.verification ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                        placeholder="000000"
                                    />
                                    {errors.verification && (
                                        <p className="mt-1 text-sm text-red-600">{errors.verification}</p>
                                    )}
                                </div>

                                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                    <div className="flex">
                                        <Info className="h-5 w-5 text-blue-400 flex-shrink-0" />
                                        <p className="ml-3 text-sm text-blue-700">
                                            Make sure to save your setup key in a secure place. You'll need it if you lose access to your authenticator app.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex space-x-3 pt-4">
                                    <button
                                        onClick={() => {
                                            setShowEnableModal(false);
                                            setVerificationCode('');
                                            setSecret('');
                                            setQrCode('');
                                            setErrors({});
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleEnable2FA}
                                        disabled={verify2FAMutation.isPending}
                                        className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        {verify2FAMutation.isPending ? 'Verifying...' : 'Enable 2FA'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Disable 2FA Modal */}
                {showDisableModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Disable Two-Factor Authentication</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    This will make your account less secure. You'll need to enter your password and a 6-digit code from your authenticator app.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                setErrors({});
                                            }}
                                            className={`block w-full px-3 py-2 pr-10 border ${
                                                errors.disable ? 'border-red-300' : 'border-gray-300'
                                            } rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                            placeholder="Enter your password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Verification Code
                                    </label>
                                    <input
                                        type="text"
                                        value={verificationCode}
                                        onChange={(e) => {
                                            setVerificationCode(e.target.value);
                                            setErrors({});
                                        }}
                                        maxLength="6"
                                        className={`block w-full px-3 py-2 border ${
                                            errors.disable ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                        placeholder="000000"
                                    />
                                </div>

                                {errors.disable && (
                                    <div className="rounded-md bg-red-50 p-3">
                                        <div className="flex">
                                            <AlertCircle className="h-4 w-4 text-red-400" />
                                            <p className="ml-2 text-sm text-red-800">{errors.disable}</p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => {
                                            setShowDisableModal(false);
                                            setVerificationCode('');
                                            setPassword('');
                                            setErrors({});
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDisable2FA}
                                        disabled={disable2FAMutation.isPending}
                                        className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                    >
                                        {disable2FAMutation.isPending ? 'Disabling...' : 'Disable 2FA'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Security;